<template>
  <div class="freight-config-routes">
    <div class="freight-config-routes__field">
      <label class="freight-config-routes__label">
        <span class="freight-config-routes__required">*</span> Tipo calculo
      </label>
      <a-select
        :value="value.tp_calc == null ? undefined : value.tp_calc"
        size="large"
        :disabled="lockHeader"
        placeholder="Selecione o tipo de calculo"
        @change="onTpCalcChange"
      >
        <a-select-option
          v-for="opt in tpCalcOptions"
          :key="opt.value"
          :value="opt.value"
        >
          {{ opt.label }}
        </a-select-option>
      </a-select>
    </div>

    <div class="freight-config-routes__routes">
      <div
        v-for="(rota, rotaIndex) in value.rota"
        :key="rota.uid"
        class="freight-config-routes__route-card"
        :class="{ 'freight-config-routes__route-card--open': rota.open }"
      >
        <div class="freight-config-routes__route-header">
          <div class="freight-config-routes__route-title">
            <span
              class="freight-config-routes__route-dot"
              :class="{
                'freight-config-routes__route-dot--done': isRouteValid(
                  rota,
                  value.tp_calc,
                ),
              }"
            />
            Rota {{ String(rota.num_rota).padStart(2, "0") }}
          </div>

          <div class="freight-config-routes__route-actions">
            <span
              v-if="roteiroMeta(rota)"
              class="freight-config-routes__route-meta"
              :title="roteiroMeta(rota)"
            >
              {{ roteiroMeta(rota) }}
            </span>

            <a-button
              v-if="rotaIndex === 0 && canAddRoute"
              class="freight-config-routes__new-route"
              @click="addRoute"
            >
              <a-icon type="plus" /> Nova rota
            </a-button>
            <a-button type="link" @click="toggleDescricao(rotaIndex)">
              {{
                rota.showDescricao ? "Ocultar descrição" : "Adicionar descrição"
              }}
            </a-button>
            <a-button
              v-if="value.rota.length > 1"
              type="link"
              class="freight-config-routes__trash"
              :aria-label="`Remover rota ${rota.num_rota}`"
              @click="removeRoute(rotaIndex)"
            >
              <a-icon type="delete" />
            </a-button>
            <button
              type="button"
              class="freight-config-routes__arrow"
              :aria-label="rota.open ? 'Recolher rota' : 'Expandir rota'"
              @click="patchRoute(rotaIndex, { open: !rota.open })"
            >
              <a-icon :type="rota.open ? 'up' : 'down'" />
            </button>
          </div>
        </div>

        <div v-show="rota.open" class="freight-config-routes__route-body">
          <div
            v-if="rota.showDescricao"
            class="freight-config-routes__descricao"
          >
            <label class="freight-config-routes__label">
              Descrição
              <span class="freight-config-routes__optional">(opcional)</span>
            </label>
            <a-textarea
              :value="rota.descricao"
              :maxLength="40"
              :rows="3"
              placeholder="Digite aqui uma descrição para essa rota"
              @change="(e) => onDescricaoChange(rotaIndex, e)"
            />
            <div class="freight-config-routes__counter">
              {{ (rota.descricao || "").length }} / 40
            </div>
          </div>

          <a-tabs
            :active-key="rota.routeTab"
            class="freight-config-routes__tabs"
            @change="(key) => patchRoute(rotaIndex, { routeTab: key })"
          >
            <a-tab-pane key="trajeto">
              <span slot="tab">
                Trajeto
                <span
                  v-if="abaPendente(rota, 'trajeto')"
                  class="freight-config-routes__tab-dot"
                  title="Campos obrigatórios pendentes"
                />
              </span>
              <div v-if="usaRoteiro" class="freight-config-routes__form-row">
                <div
                  class="freight-config-routes__field freight-config-routes__field--half"
                >
                  <label class="freight-config-routes__label">
                    <span class="freight-config-routes__required">*</span>
                    Município origem
                  </label>
                  <a-select
                    show-search
                    allow-clear
                    size="large"
                    :value="rota.municipio_origem || undefined"
                    :filter-option="false"
                    :not-found-content="municipioHint"
                    placeholder="Informe o município origem"
                    @search="onMunicipioSearch"
                    @change="
                      (v) => onLocalidadeChange(rotaIndex, 'municipio_origem', v)
                    "
                  >
                    <a-select-option
                      v-for="m in municipioOptions(rota.municipio_origem)"
                      :key="m.value"
                      :value="m.value"
                    >
                      {{ m.label }}
                    </a-select-option>
                  </a-select>
                </div>

                <div
                  class="freight-config-routes__field freight-config-routes__field--half"
                >
                  <label class="freight-config-routes__label">
                    <span class="freight-config-routes__required">*</span>
                    Roteiro
                  </label>
                  <a-select
                    show-search
                    allow-clear
                    size="large"
                    :value="rota.itinerary_id || undefined"
                    :filter-option="filterRoteiroOption"
                    placeholder="Selecione o roteiro"
                    option-label-prop="label"
                    @change="(v) => onRoteiroChange(rotaIndex, v)"
                  >
                    <a-select-option
                      v-for="r in roteiros"
                      :key="r.value"
                      :value="r.value"
                      :label="r.descr"
                    >
                      <div class="freight-config-routes__roteiro-option">
                        <strong>{{ r.descr }}</strong>
                        <span>{{ r.cd }}</span>
                      </div>
                    </a-select-option>
                  </a-select>
                </div>
              </div>

              <template v-else>
                <div class="freight-config-routes__form-row">
                  <div
                    class="freight-config-routes__field freight-config-routes__field--half"
                  >
                    <label class="freight-config-routes__label">
                      <span class="freight-config-routes__required">*</span>
                      Município origem
                    </label>
                    <a-select
                      show-search
                      allow-clear
                      size="large"
                      :value="rota.municipio_origem || undefined"
                      :disabled="!!rota.regiao_origem"
                      :filter-option="false"
                      :not-found-content="municipioHint"
                      placeholder="Informe o município origem"
                      @search="onMunicipioSearch"
                      @change="
                        (v) =>
                          onLocalidadeChange(rotaIndex, 'municipio_origem', v)
                      "
                    >
                      <a-select-option
                        v-for="m in municipioOptions(rota.municipio_origem)"
                        :key="m.value"
                        :value="m.value"
                      >
                        {{ m.label }}
                      </a-select-option>
                    </a-select>
                  </div>

                  <div
                    class="freight-config-routes__field freight-config-routes__field--half"
                  >
                    <label class="freight-config-routes__label">
                      <span class="freight-config-routes__required">*</span>
                      Município destino
                    </label>
                    <a-select
                      show-search
                      allow-clear
                      size="large"
                      :value="rota.municipio_destino || undefined"
                      :disabled="!!rota.regiao_destino"
                      :filter-option="false"
                      :not-found-content="municipioHint"
                      placeholder="Informe o município destino"
                      @search="onMunicipioSearch"
                      @change="
                        (v) =>
                          onLocalidadeChange(rotaIndex, 'municipio_destino', v)
                      "
                    >
                      <a-select-option
                        v-for="m in municipioOptions(rota.municipio_destino)"
                        :key="m.value"
                        :value="m.value"
                      >
                        {{ m.label }}
                      </a-select-option>
                    </a-select>
                  </div>

                  <div
                    class="freight-config-routes__field freight-config-routes__field--half"
                  >
                    <label class="freight-config-routes__label">
                      <span class="freight-config-routes__required">*</span>
                      Região origem
                    </label>
                    <a-select
                      show-search
                      allow-clear
                      size="large"
                      option-filter-prop="children"
                      :value="rota.regiao_origem || undefined"
                      :disabled="!!rota.municipio_origem"
                      placeholder="Informe a região origem"
                      @change="
                        (v) => onLocalidadeChange(rotaIndex, 'regiao_origem', v)
                      "
                    >
                      <a-select-option
                        v-for="r in list('regioes')"
                        :key="r.value"
                        :value="r.value"
                      >
                        {{ r.label }}
                      </a-select-option>
                    </a-select>
                  </div>

                  <div
                    class="freight-config-routes__field freight-config-routes__field--half"
                  >
                    <label class="freight-config-routes__label">
                      <span class="freight-config-routes__required">*</span>
                      Região destino
                    </label>
                    <a-select
                      show-search
                      allow-clear
                      size="large"
                      option-filter-prop="children"
                      :value="rota.regiao_destino || undefined"
                      :disabled="!!rota.municipio_destino"
                      placeholder="Informe a região destino"
                      @change="
                        (v) => onLocalidadeChange(rotaIndex, 'regiao_destino', v)
                      "
                    >
                      <a-select-option
                        v-for="r in list('regioes')"
                        :key="r.value"
                        :value="r.value"
                      >
                        {{ r.label }}
                      </a-select-option>
                    </a-select>
                  </div>
                </div>
              </template>
            </a-tab-pane>

            <a-tab-pane key="carga">
              <span slot="tab">
                Carga
                <span
                  v-if="abaPendente(rota, 'carga')"
                  class="freight-config-routes__tab-dot"
                  title="Campos obrigatórios pendentes"
                />
              </span>
              <FreightTableCarga
                :key="`carga-${rota.uid}`"
                :value="cargaForUi(rota)"
                :tipo-carga-options="list('paramTipoCarga')"
                :tipo-options="list('tpCarga')"
                :status-options="list('statusCarga')"
                @update:value="(cargas) => onCargasChange(rotaIndex, cargas)"
              />
            </a-tab-pane>

            <a-tab-pane key="caracteristicas">
              <span slot="tab">
                Características
                <span
                  v-if="abaPendente(rota, 'caracteristicas')"
                  class="freight-config-routes__tab-dot"
                  title="Campos obrigatórios pendentes"
                />
              </span>
              <div class="freight-config-routes__radio-block">
                <span class="freight-config-routes__radio-title">
                  <span class="freight-config-routes__required">*</span>
                  Tipo de operação
                </span>
                <a-radio-group
                  class="freight-config-routes__radio-group"
                  :value="rota.tp_operacao"
                  @change="
                    (e) =>
                      patchRoute(rotaIndex, { tp_operacao: e.target.value })
                  "
                >
                  <a-radio
                    v-for="o in list('tpOperacao')"
                    :key="o.value"
                    :value="o.value"
                  >
                    {{ o.label }}
                  </a-radio>
                </a-radio-group>
              </div>

              <div class="freight-config-routes__radio-block">
                <span class="freight-config-routes__radio-title">
                  <span class="freight-config-routes__required">*</span>
                  Tipo processo
                </span>
                <a-radio-group
                  class="freight-config-routes__radio-group"
                  :value="rota.tp_processo"
                  @change="
                    (e) =>
                      patchRoute(rotaIndex, { tp_processo: e.target.value })
                  "
                >
                  <a-radio
                    v-for="p in list('tpProcesso')"
                    :key="p.value"
                    :value="p.value"
                  >
                    {{ p.label }}
                  </a-radio>
                </a-radio-group>
              </div>
            </a-tab-pane>
          </a-tabs>

          <div class="freight-config-routes__divider" />

          <div class="freight-config-routes__transporte-header">
            <span class="freight-config-routes__truck-icon">
              <FreightTableTruckIcon />
            </span>
            <span class="freight-config-routes__transporte-title"
              >Transporte</span
            >
            <button
              type="button"
              class="freight-config-routes__arrow"
              :aria-label="
                rota.transporteOpen
                  ? 'Recolher transporte'
                  : 'Expandir transporte'
              "
              @click="
                patchRoute(rotaIndex, { transporteOpen: !rota.transporteOpen })
              "
            >
              <a-icon :type="rota.transporteOpen ? 'up' : 'down'" />
            </button>
          </div>

          <div
            v-show="rota.transporteOpen"
            class="freight-config-routes__transporte"
          >
            <div
              v-for="(vigencia, index) in rota.vigencia"
              :key="index"
              class="freight-config-routes__vigencia-block"
            >
              <div class="freight-config-routes__transporte-actions">
                <a-button
                  v-if="index === 0"
                  type="link"
                  @click="addVigencia(rotaIndex)"
                >
                  <a-icon type="plus" /> Nova vigência
                </a-button>
                <a-button
                  v-else
                  type="link"
                  class="freight-config-routes__trash"
                  :aria-label="`Remover vigência ${index + 1}`"
                  @click="removeVigencia(rotaIndex, index)"
                >
                  <a-icon type="delete" />
                </a-button>
              </div>

              <div class="freight-config-routes__vigencia-card">
              <div class="freight-config-routes__vigencia">
              <div class="freight-config-routes__field">
                <label class="freight-config-routes__label">
                  <span class="freight-config-routes__required">*</span>
                  Vigência
                </label>

                <a-range-picker
                  v-if="editingVigencia === `${rotaIndex}-${index}`"
                  :open="true"
                  :value="toRange(vigencia)"
                  :locale="datePickerLocale"
                  :format="DATE_FORMAT"
                  :placeholder="['Data inicial', 'Data final']"
                  separator="→"
                  :allow-clear="false"
                  @calendarChange="
                    (dates) => onVigenciaCalendarChange(rotaIndex, index, dates)
                  "
                  @change="(dates) => onVigenciaChange(rotaIndex, index, dates)"
                  @openChange="
                    (open) => onVigenciaOpenChange(rotaIndex, index, open)
                  "
                />
                <div
                  v-else
                  class="freight-config-routes__vigencia-field"
                  role="button"
                  tabindex="0"
                  @click="openVigencia(rotaIndex, index)"
                  @keydown.enter.prevent="openVigencia(rotaIndex, index)"
                  @keydown.space.prevent="openVigencia(rotaIndex, index)"
                >
                  <template
                    v-if="vigencia.dt_vigencia_ini || vigencia.dt_vigencia_fim"
                  >
                    <span
                      v-for="campo in VIGENCIA_DATE_FIELDS"
                      v-show="vigencia[campo]"
                      :key="campo"
                      class="freight-config-routes__vigencia-tag"
                    >
                      {{ vigencia[campo] }}
                      <a-icon
                        type="close"
                        :aria-label="`Remover ${vigencia[campo]}`"
                        @click.stop="clearVigenciaDate(rotaIndex, index, campo)"
                      />
                    </span>
                  </template>
                  <span
                    v-else
                    class="freight-config-routes__vigencia-placeholder"
                  >
                    Informe a vigência definida
                  </span>
                </div>
              </div>

              <div v-if="!porFaixa" class="freight-config-routes__field">
                <label class="freight-config-routes__label">
                  <span class="freight-config-routes__required">*</span>
                  Valor frete
                </label>
                <a-input
                  :value="formatCurrency(vigencia.vlr)"
                  placeholder="Informe o valor do frete"
                  @change="(e) => onVlrChange(rotaIndex, index, e)"
                />
              </div>

              <div v-if="usaVeiculo" class="freight-config-routes__field">
                <label class="freight-config-routes__label">
                  <span class="freight-config-routes__required">*</span> Veiculo
                </label>
                <FreightTableVehicleSelect
                  :value="vehiclesOf(vigencia)"
                  :options="list('veiculos')"
                  @change="(ids) => onVeiculoChange(rotaIndex, index, ids)"
                />
              </div>

              </div>

              <FreightTableValorSaidaVeiculo
                v-if="faixaPesoPorVeiculo"
                class="freight-config-routes__valor-saida"
                :ativa="vigencia.saidaVeiculoAtiva"
                :value="vigencia.valoresSaida"
                :veiculos="list('veiculos')"
                @update:ativa="
                  (checked) =>
                    patchVigencia(rotaIndex, index, {
                      saidaVeiculoAtiva: checked,
                    })
                "
                @update:value="
                  (linhas) =>
                    patchVigencia(rotaIndex, index, { valoresSaida: linhas })
                "
              />
              </div>

              <div v-if="porFaixa" class="freight-config-routes__faixas">
                <FreightTableRouteFaixas
                  :value="vigencia.calculo"
                  :title="rangeBlockTitle"
                  :veiculo-error="faixaVeiculoError(vigencia)"
                  @update:value="
                    (faixas) => onFaixasChange(rotaIndex, index, faixas)
                  "
                />
              </div>

              <FreightTableRouteTaxes
                class="freight-config-routes__taxes"
                :value="vigencia"
                :status="rota.status === undefined ? 1 : rota.status"
                @patch="(partial) => patchVigencia(rotaIndex, index, partial)"
                @status-change="(status) => patchRoute(rotaIndex, { status })"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import "moment/locale/pt-br";
import ptBR from "ant-design-vue/lib/date-picker/locale/pt_BR";
import FreightTableCarga from "@/modules/FreightTables/components/Details/FreightTableCarga.vue";
import FreightTableRouteTaxes from "./FreightTableRouteTaxes.vue";
import { copyTaxas } from "../../utils/validityTaxes";
import FreightTableRouteFaixas from "./FreightTableRouteFaixas.vue";
import FreightTableValorSaidaVeiculo from "./FreightTableValorSaidaVeiculo.vue";
import FreightTableVehicleSelect from "./FreightTableVehicleSelect.vue";
import FreightTableTruckIcon from "../FreightTableTruckIcon.vue";
import { mapState } from "vuex";
import {
  calcIsFaixaPesoPorVeiculo,
  calcRequiresRanges,
  calcUsesItinerary,
  calcUsesVehicle,
  describeRangeBlockTitle,
  mapTpCalcOptionLabel,
} from "../../constants/freightTableOptions";
import {
  createEmptyRoute,
  createEmptyVigencia,
  isRouteEmpty,
  isRouteValid,
  routeGaps,
  validateValorVariavelVeiculo,
  validityVehicles,
} from "../../services/freightTableConfigForm";

const DATE_FORMAT = "DD/MM/YYYY";

const MUNICIPIO_OPTIONS_LIMIT = 50;

const VIGENCIA_DATE_FIELDS = ["dt_vigencia_ini", "dt_vigencia_fim"];

export default {
  name: "FreightTableConfigRoutesSection",
  components: {
    FreightTableCarga,
    FreightTableRouteTaxes,
    FreightTableRouteFaixas,
    FreightTableValorSaidaVeiculo,
    FreightTableVehicleSelect,
    FreightTableTruckIcon,
  },
  props: {
    value: {
      type: Object,
      required: true,
    },
    lockHeader: {
      type: Boolean,
      default: false,
    },
    canAddRoute: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      DATE_FORMAT,
      VIGENCIA_DATE_FIELDS,
      datePickerLocale: ptBR,
      editingVigencia: null,
      vigenciaFimEscolhido: false,
      municipioSearch: "",
    };
  },
  computed: {
    ...mapState("freightTables", ["options", "itineraryOptions"]),
    municipioHint() {
      return this.list("municipios").length
        ? "Nenhum município encontrado"
        : "Carregando municípios…";
    },
    tpCalcOptions() {
      return this.list("tpCalc").map(mapTpCalcOptionLabel);
    },
    // Viagem define a rota por roteiro; nos outros tipos o trajeto é
    // origem→destino, por município ou região (§4.8.7).
    usaRoteiro() {
      return calcUsesItinerary(this.value.tp_calc);
    },
    // Faixa de peso/KM/volume: o valor mora em cada faixa da vigência, não num
    // "Valor frete" único (§4.8).
    porFaixa() {
      return calcRequiresRanges(this.value.tp_calc);
    },
    faixaPesoPorVeiculo() {
      return calcIsFaixaPesoPorVeiculo(this.value.tp_calc);
    },
    rangeBlockTitle() {
      return describeRangeBlockTitle(this.value.tp_calc);
    },
    usaVeiculo() {
      return calcUsesVehicle(this.value.tp_calc);
    },
    roteiros() {
      return this.itineraryOptions || [];
    },
  },
  methods: {
    isRouteValid,
    /** "<cd> • N paradas" do roteiro escolhido, no header da rota. */
    roteiroMeta(rota) {
      const roteiro = rota.itinerary_id
        ? this.roteiros.find((r) => r.value === rota.itinerary_id)
        : null;
      if (!roteiro) return "";
      const paradas = roteiro.paradas;
      if (paradas == null) return roteiro.cd;
      return `${roteiro.cd} • ${paradas === 1 ? "1 parada" : `${paradas} paradas`}`;
    },
    /** Marcador na aba: os obrigatórios de Carga/Características ficam escondidos. */
    abaPendente(rota, aba) {
      return routeGaps(rota, this.value.tp_calc)[aba].length > 0;
    },
    /** Listas de domínio vêm da API e são null enquanto carregam. */
    list(name) {
      return (this.options && this.options[name]) || [];
    },
    // FreightTableCarga usa { tipoCarga, tipo, status }; o form/API usa
    // { param_tipo_carga, tp_carga, status }.
    cargaForUi(rota) {
      return (rota.carga || []).map((item) => ({
        tipoCarga:
          item.tipoCarga != null ? item.tipoCarga : item.param_tipo_carga,
        tipo: item.tipo != null ? item.tipo : item.tp_carga,
        status: item.status != null ? item.status : null,
      }));
    },
    emit(next) {
      this.$emit("input", next);
    },
    patch(partial) {
      this.emit({ ...this.value, ...partial });
    },
    /**
     * Roteiro e destino são mutuamente exclusivos no §4.8.7, e o roteiro é o
     * gatilho: se sobrasse um itinerary_id de quando o tipo era Viagem, o back
     * ignoraria o destino recém-preenchido. Trocar o tipo limpa o lado que
     * deixou de existir na tela.
     */
    onTpCalcChange(tpCalc) {
      const paraRoteiro = calcUsesItinerary(tpCalc);
      const rota = this.value.rota.map((item) =>
        paraRoteiro
          ? {
              ...item,
              regiao_origem: null,
              municipio_destino: null,
              regiao_destino: null,
            }
          : { ...item, itinerary_id: null },
      );
      this.emit({ ...this.value, tp_calc: tpCalc, rota });
    },
    patchRoute(rotaIndex, partial) {
      const rota = this.value.rota.map((item, i) =>
        i === rotaIndex ? { ...item, ...partial } : item,
      );
      this.emit({ ...this.value, rota });
    },
    onCargasChange(rotaIndex, cargas) {
      const mapped = (Array.isArray(cargas) ? cargas : []).map((item) => ({
        param_tipo_carga: item.tipoCarga != null ? item.tipoCarga : null,
        tp_carga: item.tipo != null ? item.tipo : null,
        status: item.status != null ? item.status : null,
      }));
      this.patchRoute(rotaIndex, { carga: mapped });
    },
    patchVigencia(rotaIndex, vigIndex, partial) {
      const atual = this.value.rota[rotaIndex];
      const vigencia = atual.vigencia.map((item, i) =>
        i === vigIndex ? { ...item, ...partial } : item,
      );
      this.patchRoute(rotaIndex, { vigencia });
    },
    // A nova rota nasce como mais um bloco no fim da lista, aberto, com os
    // mesmos campos e o mesmo botão de remover da rota 1 (ADR-0013 §1).
    addRoute() {
      const rota = this.value.rota.map((item) => ({ ...item, open: false }));
      rota.push(createEmptyRoute(rota.length + 1));
      this.emit({ ...this.value, rota });
    },
    removeRoute(rotaIndex) {
      if (this.value.rota.length <= 1) return;
      const alvo = this.value.rota[rotaIndex];
      if (isRouteEmpty(alvo)) {
        this.applyRemoveRoute(rotaIndex);
        return;
      }
      this.$confirm({
        title: `Remover a rota ${String(alvo.num_rota).padStart(2, "0")}?`,
        content:
          "Os dados de trajeto, vigência e taxas dessa rota serão descartados.",
        okText: "Remover",
        okType: "danger",
        cancelText: "Voltar",
        onOk: () => this.applyRemoveRoute(rotaIndex),
      });
    },
    applyRemoveRoute(rotaIndex) {
      const rota = this.value.rota
        .filter((_, i) => i !== rotaIndex)
        .map((item, i) => ({ ...item, num_rota: i + 1 }));
      this.editingVigencia = null;
      this.emit({ ...this.value, rota });
    },
    toggleDescricao(rotaIndex) {
      const rota = this.value.rota[rotaIndex];
      this.patchRoute(rotaIndex, { showDescricao: !rota.showDescricao });
    },
    onDescricaoChange(rotaIndex, e) {
      const value = String((e && e.target && e.target.value) || "").slice(
        0,
        40,
      );
      this.patchRoute(rotaIndex, { descricao: value });
    },
    onMunicipioSearch(value) {
      this.municipioSearch = value;
    },
    // A lista vem inteira do jsonlistgeral (~5,5 mil municípios) e o a-select do
    // antd 1.x não virtualiza: renderizar tudo trava ao abrir. Filtra pela busca
    // e corta; a opção selecionada entra sempre, senão o campo mostraria o id.
    municipioOptions(selectedId) {
      const todos = this.list("municipios");
      const busca = String(this.municipioSearch || "")
        .trim()
        .toLowerCase();
      const selecionado = selectedId
        ? todos.find((m) => m.value === selectedId)
        : null;
      const encontrados = (
        busca
          ? todos.filter((m) => m.label.toLowerCase().includes(busca))
          : todos
      )
        .filter((m) => !selecionado || m.value !== selecionado.value)
        .slice(0, MUNICIPIO_OPTIONS_LIMIT);

      return selecionado ? [selecionado].concat(encontrados) : encontrados;
    },
    filterRoteiroOption(input, option) {
      const id = option.componentOptions.propsData.value;
      const roteiro = this.roteiros.find((r) => r.value === id);
      if (!roteiro) return false;
      const q = String(input).toLowerCase();
      return (
        String(roteiro.descr || "")
          .toLowerCase()
          .includes(q) ||
        String(roteiro.cd || "")
          .toLowerCase()
          .includes(q)
      );
    },
    // Município e região do mesmo lado são exclusivos: o back grava um só, e
    // tp_origem/tp_destino saem de qual foi preenchido. Escolher um limpa o
    // outro em vez de só desabilitar, senão trocar deixaria os dois gravados.
    onLocalidadeChange(rotaIndex, campo, valor) {
      const opostos = {
        municipio_origem: "regiao_origem",
        regiao_origem: "municipio_origem",
        municipio_destino: "regiao_destino",
        regiao_destino: "municipio_destino",
      };
      const patch = {};
      patch[campo] = valor || null;
      patch[opostos[campo]] = null;
      this.patchRoute(rotaIndex, patch);
    },
    onFaixasChange(rotaIndex, vigIndex, faixas) {
      this.patchVigencia(rotaIndex, vigIndex, { calculo: faixas });
    },
    onRoteiroChange(rotaIndex, itineraryId) {
      this.patchRoute(rotaIndex, { itinerary_id: itineraryId || null });
    },
    toMoment(value) {
      if (!value) return null;
      const m = moment(value, DATE_FORMAT, true);
      return m.isValid() ? m : null;
    },
    toRange(vigencia) {
      const ini = this.toMoment(vigencia.dt_vigencia_ini);
      const fim = this.toMoment(vigencia.dt_vigencia_fim);
      if (!ini && !fim) return [];
      return [ini, fim];
    },
    openVigencia(rotaIndex, vigIndex) {
      this.editingVigencia = `${rotaIndex}-${vigIndex}`;
      this.vigenciaFimEscolhido = false;
    },
    /**
     * No ant-design-vue 1.x o `change` do range-picker só sai com as duas
     * pontas escolhidas; o primeiro clique emite apenas `calendarChange`, e com
     * um item só. É esse tamanho que separa a vigência aberta (um clique) do
     * intervalo fechado — inclusive o de um dia, em que as duas pontas caem no
     * mesmo dia e o par ficaria idêntico ao do clique único (TMS-8016).
     */
    onVigenciaCalendarChange(rotaIndex, vigIndex, dates) {
      const [ini, fim] = dates || [];
      if (!ini) return;

      this.vigenciaFimEscolhido = !!fim;
      this.patchVigencia(rotaIndex, vigIndex, {
        dt_vigencia_ini: ini.format(DATE_FORMAT),
        dt_vigencia_fim: fim ? fim.format(DATE_FORMAT) : null,
      });
    },
    onVigenciaChange(rotaIndex, vigIndex, dates) {
      const [ini, fim] = dates || [];
      const fechouIntervalo = this.vigenciaFimEscolhido && !!fim;

      this.patchVigencia(rotaIndex, vigIndex, {
        dt_vigencia_ini: ini ? ini.format(DATE_FORMAT) : null,
        dt_vigencia_fim: fechouIntervalo ? fim.format(DATE_FORMAT) : null,
      });

      this.editingVigencia = null;
    },
    onVigenciaOpenChange(rotaIndex, vigIndex, open) {
      if (!open && this.editingVigencia === `${rotaIndex}-${vigIndex}`) {
        this.editingVigencia = null;
      }
    },
    clearVigenciaDate(rotaIndex, vigIndex, campo) {
      // Fim sem início não é vigência: remover o início derruba o par.
      const patch =
        campo === "dt_vigencia_ini"
          ? { dt_vigencia_ini: null, dt_vigencia_fim: null }
          : { [campo]: null };
      this.patchVigencia(rotaIndex, vigIndex, patch);
    },

    formatCurrency(value) {
      if (value === null || value === undefined || value === "") return "";
      const num = Number(value);
      if (!Number.isFinite(num)) return "";
      return num.toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
    },
    onVlrChange(rotaIndex, vigIndex, e) {
      const raw = String((e && e.target ? e.target.value : e) || "");
      const digits = raw.replace(/\D/g, "");
      const vlr = digits === "" ? null : Number(digits) / 100;
      const vigencia = this.value.rota[rotaIndex].vigencia[vigIndex];
      const calculo = [{ ...(vigencia.calculo[0] || {}), vlr }];
      this.patchVigencia(rotaIndex, vigIndex, { vlr, calculo });
    },
    vehiclesOf(vigencia) {
      return validityVehicles(vigencia);
    },
    faixaVeiculoError(vigencia) {
      const erros = validateValorVariavelVeiculo(vigencia, this.value.tp_calc);
      return erros.length ? erros[0].message : "";
    },
    onVeiculoChange(rotaIndex, vigIndex, ids) {
      // transp[] é derivado no mapper a partir daqui — não se escreve no estado.
      this.patchVigencia(rotaIndex, vigIndex, { tp_rodado: ids || [] });
    },
    addVigencia(rotaIndex) {
      const rota = this.value.rota[rotaIndex];
      this.patchRoute(rotaIndex, {
        vigencia: [
          ...rota.vigencia,
          {
            ...createEmptyVigencia(),
            ...copyTaxas(rota.vigencia[rota.vigencia.length - 1]),
          },
        ],
      });
    },
    removeVigencia(rotaIndex, vigIndex) {
      const rota = this.value.rota[rotaIndex];
      if (rota.vigencia.length <= 1) return;
      this.editingVigencia = null;
      this.patchRoute(rotaIndex, {
        vigencia: rota.vigencia.filter((_, i) => i !== vigIndex),
      });
    },
  },
};
</script>

<style scoped lang="less">
@import "~@/shared/styles/variables.less";

.freight-config-routes {
  font-family: @font-primary;

  &__label {
    display: block;
    padding-bottom: 8px;
    color: rgba(0, 0, 0, 0.88);
    font-size: 14px;
    line-height: 22px;
  }

  &__required {
    color: @color-error;
  }

  &__optional {
    color: rgba(0, 0, 0, 0.45);
  }

  &__field {
    flex: 1 1 180px;
    min-width: 0;

    &--half {
      flex: 1 1 180px;
    }
  }

  /deep/ .ant-select,
  /deep/ .ant-calendar-picker {
    width: 100%;
  }

  /deep/ .freight-vehicle-select__trigger {
    height: 32px;
  }

  &__divider {
    padding: 4px 0;
    margin: 8px 0;

    &::after {
      content: "";
      display: block;
      height: 1px;
      background: rgba(0, 0, 0, 0.04);
    }
  }

  &__radio-block {
    margin-bottom: 20px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__radio-title {
    display: block;
    padding: 4px 8px;
    font-size: 12px;
    line-height: 20px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.88);
  }

  &__radio-group {
    display: flex;
    flex-wrap: wrap;
    align-items: center;

    /deep/ .ant-radio-wrapper {
      display: inline-flex;
      align-items: center;
      margin-right: 0;
      padding: 8px 12px;
      border-radius: 6px;
      font-size: 14px;
      line-height: 22px;
    }

    /deep/ .ant-radio-checked .ant-radio-inner {
      border-color: @color-primary;
      background: @color-primary;

      &::after {
        background: #ffffff;
        transform: scale(0.625);
      }
    }

    /deep/ .ant-radio-checked::after,
    /deep/ .ant-radio:hover .ant-radio-inner,
    /deep/ .ant-radio-input:focus + .ant-radio-inner {
      border-color: @color-primary;
    }
  }

  &__routes {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 24px;
  }

  &__route-card {
    box-sizing: border-box;
    background: #ffffff;
    border: 1px solid #f0f0f0;
    border-radius: 12px;

    &--open {
      padding-bottom: 8px;

      .freight-config-routes__route-header {
        border-radius: 12px 12px 0 0;
      }
    }
  }

  &__route-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    min-height: 52px;
    padding: 0 16px;
    background: rgba(0, 0, 0, 0.02);
    border-radius: 12px;
  }

  &__route-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 600;
    font-size: 14px;
    line-height: 22px;
    color: rgba(0, 0, 0, 0.88);
  }

  &__tab-dot {
    display: inline-block;
    width: 6px;
    height: 6px;
    margin-left: 4px;
    vertical-align: middle;
    border-radius: 50%;
    background: #faad14;
  }

  &__route-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.25);

    &--done {
      background: @color-success-light;
    }
  }

  &__route-actions {
    display: flex;
    align-items: center;
    gap: 4px;
    min-width: 0;
    padding: 4px 0;

    /deep/ .ant-btn-link {
      color: @color-primary;
      padding: 0 15px;
    }
  }

  &__new-route {
    border-radius: 6px;
    box-shadow: 0 2px 0 rgba(0, 0, 0, 0.02);
  }

  // Em telas estreitas o header aperta: a meta encolhe com reticências em vez
  // de empurrar os botões para fora.
  &__route-meta {
    flex: 0 1 auto;
    min-width: 0;
    margin-right: 8px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 14px;
    line-height: 22px;
    color: rgba(0, 0, 0, 0.45);
  }

  &__arrow {
    display: inline-flex;
    align-items: center;
    padding: 0 8px;
    border: 0;
    background: transparent;
    color: rgba(0, 0, 0, 0.45);
    cursor: pointer;
  }

  &__route-body {
    padding: 0 16px 8px;
  }

  &__descricao {
    padding: 12px 0 4px;
  }

  &__counter {
    text-align: right;
    font-size: 14px;
    line-height: 22px;
    color: rgba(0, 0, 0, 0.45);
    margin-top: 2px;
  }

  &__tabs {
    /deep/ .ant-tabs-bar {
      margin-bottom: 12px;
      border-bottom-color: #f0f0f0;
    }

    /deep/ .ant-tabs-nav .ant-tabs-tab {
      padding: 12px 0;
      margin-right: 32px;
      font-size: 14px;
      line-height: 22px;
    }

    /deep/ .ant-tabs-ink-bar {
      height: 2px;
      background: @color-primary;
    }

    /deep/ .ant-tabs-tab-active {
      color: @color-primary;
      font-weight: 600;
    }
  }

  &__form-row {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 12px;
    padding-bottom: 8px;
  }

  &__roteiro-option {
    display: flex;
    flex-direction: column;
    line-height: 1.3;

    span {
      font-size: 12px;
      color: rgba(0, 0, 0, 0.65);
    }
  }

  &__transporte-header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 4px 16px;
  }

  &__transporte-title {
    flex: 1 1 auto;
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
    color: rgba(0, 0, 0, 0.88);
  }

  &__truck-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 20px;
    width: 20px;
    height: 20px;
    border-radius: 99px;
    background: #ecf3f9;
    color: @color-primary;
    font-size: 12px;
  }

  &__transporte {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &__transporte-actions {
    display: flex;
    justify-content: flex-end;

    /deep/ .ant-btn-link {
      color: @color-primary;
      padding: 0 15px;
    }
  }

  // Divider entre os campos da vigência e o bloco de faixa de peso
  // (frame 1308-32746: linha de largura total, preta a 4%).
  &__faixas {
    margin-top: 12px;
    border-top: 1px solid rgba(0, 0, 0, 0.04);
  }

  &__taxes {
    margin-top: 12px;
    padding: 0 10px;
  }

  &__vigencia-block {
    padding: 8px 12px 16px;
    background: rgba(0, 0, 0, 0.02);
    border: 1px solid @color-border-light;
    border-radius: 12px;
  }

  &__vigencia-card {
    padding-bottom: 12px;
  }

  &__vigencia {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 12px;
    background: transparent;
    border-radius: 0;
    padding: 12px 10px 0;
    margin-top: 0;
  }

  &__valor-saida {
    margin: 8px 10px 0;
  }

  &__vigencia-field {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px;
    box-sizing: border-box;
    min-height: 32px;
    padding: 3px 8px;
    background: #ffffff;
    border: 1px solid #d9d9d9;
    border-radius: 6px;
    cursor: pointer;
    transition: border-color 0.3s;

    &:hover,
    &:focus {
      border-color: @color-primary;
      outline: none;
    }
  }

  &__vigencia-tag {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    height: 24px;
    padding: 0 8px;
    background: @color-tag-info-bg;
    border-radius: 4px;
    color: @color-primary;
    font-size: 14px;
    line-height: 22px;
    white-space: nowrap;

    .anticon {
      font-size: 12px;
      color: rgba(0, 0, 0, 0.45);
      cursor: pointer;

      &:hover {
        color: rgba(0, 0, 0, 0.75);
      }
    }
  }

  &__vigencia-placeholder {
    color: rgba(0, 0, 0, 0.25);
    font-size: 14px;
    line-height: 22px;
  }

  &__trash {
    flex: 0 0 auto;
    color: #d9363e !important;

    &[disabled] {
      color: rgba(0, 0, 0, 0.25) !important;
    }
  }

  /deep/ .ant-input,
  /deep/ .ant-select-selection,
  /deep/ .ant-calendar-picker-input {
    border-radius: 6px;
  }

  /deep/ .ant-input-lg,
  /deep/ .ant-select-lg .ant-select-selection {
    border-radius: 8px;
  }

  /deep/ .ant-switch-checked {
    background: @color-primary;
  }

  /deep/ .ant-select-selection__choice {
    background: #ecf3f9;
    border-radius: 4px;
    color: @color-primary;
  }
}
</style>

<style lang="less">
@import "~@/modules/shared/styles/form-controls.less";

.freight-config-routes {
  .module-form-controls();
}
</style>
